import { useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import ReceiptModal from '@/components/feature/ReceiptModal';
import { useOrders } from '@/hooks/useOrders';
import { updateOrderStatus, confirmProductionOrder, markOrderAsPaid, type Order } from '@/services/orders';
import { METHOD_LABELS } from '@/services/payments';
import { downloadCsv } from '@/lib/csv';
import ProductionOrderModal, { PRODUCTION_STATUSES } from './components/ProductionOrderModal';

const STATUS_ORDER = ['all', 'para_produccion', 'producido', 'listo_envio', 'entregado', 'cancelled'];
const STATUS_LABELS: Record<string, string> = {
  all: 'Todos',
  para_produccion: 'Para producción',
  producido: 'Producido',
  listo_envio: 'Listo para enviar',
  entregado: 'Entregado',
  cancelled: 'Cancelado',
};

// Un pedido de producción solo debería transitar los estados del flujo de
// producción. Si por datos heredados quedó en un estado de checkout (por
// ejemplo un pago en línea aprobado antes de alinearse), lo mostramos como
// "Para producción" para que sea visible, filtrable y concuerde con su
// etiqueta de pago.
function effectiveProductionStatus(status: string): string {
  return PRODUCTION_STATUSES[status] ? status : 'para_produccion';
}

export default function ProduccionPage() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { data: orders, loading, error, refetch } = useOrders();
  const [editing, setEditing] = useState<Order | null>(null);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const [receiptOrder, setReceiptOrder] = useState<Order | null>(null);

  async function handleMarkAsPaid(orderId: string) {
    setActionError('');
    setMarkingPaid(orderId);
    const { error: markErr } = await markOrderAsPaid(orderId);
    setMarkingPaid(null);
    if (markErr) {
      setActionError(markErr);
    } else {
      setActionSuccess('Pago marcado como recibido. El pedido hermano (si existe) también fue actualizado.');
      refetch();
    }
  }

  const productionOrders = orders.filter((o) => o.production);

  async function handleStatusChange(id: string, status: string) {
    setActionError('');
    setActionSuccess('');

    if (status === 'entregado') {
      const res = await confirmProductionOrder(id, 'cash');
      if (!res.success) {
        setActionError(res.error || 'No se pudo entregar el pedido.');
        refetch();
        return;
      }
      setActionSuccess(`Pedido entregado y convertido en venta ${res.saleNumber}.`);
    } else {
      const { error: updateError } = await updateOrderStatus(id, status);
      if (updateError) setActionError(updateError);
    }
    refetch();
  }

  function exportProduction() {
    downloadCsv(
      'produccion',
      ['Pedido', 'Cliente', 'Productos', 'Total', 'Estado', 'Fecha'],
      filtered.map((o) => [
        o.orderNumber,
        o.customerName,
        o.items.map((i) => `${i.productName} (${i.sizeLabel}) × ${i.quantity}`).join(' | '),
        o.total,
        STATUS_LABELS[o.status] ?? o.status,
        o.createdAt ? new Date(o.createdAt).toLocaleDateString('es-CO') : '',
      ]),
    );
  }

  const filtered = productionOrders.filter((o) => {
    if (filter !== 'all' && effectiveProductionStatus(o.status) !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!o.orderNumber.toLowerCase().includes(q) && !o.customerName.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  const counts: Record<string, number> = {};
  STATUS_ORDER.forEach((s) => {
    counts[s] =
      s === 'all'
        ? productionOrders.length
        : productionOrders.filter((o) => effectiveProductionStatus(o.status) === s).length;
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {actionError && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{actionError}</span>
          </div>
        )}
        {actionSuccess && (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <i className="ri-checkbox-circle-line text-emerald-600 text-lg"></i>
            <p className="text-sm text-emerald-800 font-medium">{actionSuccess}</p>
            <button
              onClick={() => setActionSuccess('')}
              className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Producción</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{productionOrders.length} pedidos para producción</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap">
                Se manda a hacer
              </span>
            </p>
          </div>
          <button
            onClick={exportProduction}
            className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-download-line mr-1.5"></i>Exportar
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === s
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {STATUS_LABELS[s]} ({counts[s] || 0})
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar pedido o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Pedido</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Productos</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Medio de pago</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Total</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Fecha</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const displayStatus = effectiveProductionStatus(order.status);
                  const s = PRODUCTION_STATUSES[displayStatus] || {
                    label: displayStatus,
                    color: 'text-foreground-500',
                    bg: 'bg-background-100',
                  };
                  return (
                    <tr key={order.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-accent-600 font-medium whitespace-nowrap">{order.orderNumber}</span>
                          <button
                            onClick={() => setReceiptOrder(order)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                            title="Descargar comprobante"
                            aria-label="Descargar comprobante"
                          >
                            <i className="ri-download-2-line text-sm"></i>
                          </button>
                        </div>
                        {order.saleNumber && (
                          <Link
                            to="/admin/ventas"
                            className="mt-0.5 block text-xs text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap"
                          >
                            <i className="ri-receipt-line mr-0.5"></i>Venta: {order.saleNumber}
                          </Link>
                        )}
                        {order.siblingOrderNumber && (
                          <Link
                            to="/admin/pedidos"
                            className="mt-0.5 block text-xs text-amber-600 hover:text-amber-700 font-medium whitespace-nowrap"
                          >
                            <i className="ri-links-line mr-0.5"></i>Grupo: {order.siblingOrderNumber}
                          </Link>
                        )}
                        {order.paymentStatus === 'approved' &&
                          order.status !== 'entregado' &&
                          order.status !== 'cancelled' && (
                            <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500 text-white whitespace-nowrap">
                              <i className="ri-checkbox-circle-fill"></i>
                              Pagado · En producción
                            </span>
                          )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-foreground-900 font-medium text-sm">{order.customerName || '—'}</p>
                          {order.customerCity && (
                            <p className="text-xs text-foreground-500">{order.customerCity}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <div className="space-y-0.5">
                          {order.items.slice(0, 2).map((item) => (
                            <p key={item.id} className="text-xs text-foreground-700">
                              {item.productName} — {item.sizeLabel} × {item.quantity}
                            </p>
                          ))}
                          {order.items.length > 2 && (
                            <p className="text-xs text-foreground-400">+{order.items.length - 2} más</p>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                          {order.paymentMethod === 'cash'
                          ? 'Contra entrega'
                          : METHOD_LABELS[order.paymentMethod] || order.paymentMethod || '—'}
                        </span>
                        {order.paymentStatus === 'approved' && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                            Pagado
                          </span>
                        )}
                        {order.paymentStatus === 'pending' && order.paymentMethod && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                            Pendiente
                          </span>
                        )}
                        {order.paymentStatus === 'pending' && order.paymentMethod && (
                          <button
                            onClick={() => handleMarkAsPaid(order.id)}
                            disabled={markingPaid === order.id}
                            className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap cursor-pointer disabled:opacity-60"
                          >
                            {markingPaid === order.id ? (
                              <><i className="ri-loader-4-line animate-spin"></i>Marcando...</>
                            ) : (
                              <><i className="ri-checkbox-circle-line"></i>Marcar como pagado</>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-foreground-900 font-medium whitespace-nowrap">
                        ${order.total.toLocaleString()}
                      </td>
                      <td className="px-5 py-3.5">
                        <select
                          value={displayStatus}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${s.color} ${s.bg}`}
                        >
                          {Object.keys(PRODUCTION_STATUSES).map((key) => (
                            <option key={key} value={key}>
                              {PRODUCTION_STATUSES[key].label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-xs text-foreground-500 whitespace-nowrap">
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleDateString('es-CO', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => setEditing(order)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                          title="Editar pedido"
                          aria-label="Editar pedido"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-tools-line text-3xl block mb-2"></i>
              <p className="text-sm">No hay pedidos para producción</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              <p className="text-sm">Cargando producción...</p>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ProductionOrderModal
          order={editing}
          onClose={() => setEditing(null)}
          onChanged={refetch}
          onStatusChange={handleStatusChange}
        />
      )}

      {receiptOrder && (
        <ReceiptModal
          receipt={{
            title: 'Comprobante de Pedido',
            number: receiptOrder.orderNumber,
            date: receiptOrder.createdAt,
            customerName: receiptOrder.customerName,
            customerPhone: receiptOrder.customerPhone,
            customerCity: receiptOrder.customerCity,
            paymentMethod: receiptOrder.paymentMethod,
            paymentStatus: receiptOrder.paymentStatus,
            items: receiptOrder.items.map((it) => ({
              productName: it.productName,
              sizeLabel: it.sizeLabel,
              quantity: it.quantity,
              unitPrice: it.unitPrice,
              subtotal: it.quantity * it.unitPrice,
            })),
            total: receiptOrder.total,
            notes: receiptOrder.notes,
          }}
          onClose={() => setReceiptOrder(null)}
        />
      )}
    </AdminLayout>
  );
}